import {
  POLYMARKET_CATALOG_DOMAIN,
  POLYMARKET_PROXY_DOMAIN,
  proxyHttpUrl,
} from '../constants/api';
import { PolymarketApplicableMarket } from '../types/polymarket';

export interface PolymarketMarketsPage {
  markets: PolymarketApplicableMarket[];
  nextCursor: string | null;
  total: number;
  updatedAt: string | null;
}

export interface PolymarketCatalogManifest {
  schemaVersion: 1;
  updatedAt: string;
  snapshotUrl: string;
  total: number;
  bytes: number | null;
}

export interface PolymarketCatalogSnapshot {
  schemaVersion: 1;
  updatedAt: string;
  total: number;
  markets: PolymarketApplicableMarket[];
}

interface FetchPolymarketMarketsPageParams {
  limit?: number;
  cursor?: string | null;
  q?: string;
  seed?: boolean;
  signal?: AbortSignal;
}

const POLYMARKET_MARKETS_BASE_URL = proxyHttpUrl(POLYMARKET_PROXY_DOMAIN);
const POLYMARKET_CATALOG_BASE_URL = proxyHttpUrl(POLYMARKET_CATALOG_DOMAIN);

// Proxy-served snapshot; fallback when the manifest's CDN snapshot is missing.
export const POLYMARKET_CATALOG_SNAPSHOT_FALLBACK_PATH = '/catalog/snapshot';

const internedTagLists = new Map<string, readonly string[]>();
const MAX_INTERNED_TAG_LISTS = 50_000;
const NO_TAGS: readonly string[] = Object.freeze([]);

// Every market of an event carries the same tag list, so one frozen array per
// distinct list keeps tens of thousands of markets from each holding a copy.
export const internPolymarketTags = (
  tags: readonly unknown[] | null | undefined,
): readonly string[] => {
  if (!tags?.length) return NO_TAGS;
  const labels = [
    ...new Set(
      tags
        .filter((tag): tag is string => typeof tag === 'string')
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ];
  if (!labels.length) return NO_TAGS;
  const key = labels.join('\u0000');
  const existing = internedTagLists.get(key);
  if (existing) return existing;
  const interned = Object.freeze(labels);
  if (internedTagLists.size >= MAX_INTERNED_TAG_LISTS) internedTagLists.clear();
  internedTagLists.set(key, interned);
  return interned;
};

// The catalog/page/by-id endpoints return rich market objects that include a
// description (and other server-side cruft). The seed catalog is the
// polymatcher corpus (empty-search / CDN), so retaining descriptions inflated
// the heap. Keep only the fields the terminal actually needs; the description
// is fetched lazily per selected market via fetchPolymarketMarketDescription.
export const normalizePolymarketMarket = (
  market: PolymarketApplicableMarket,
): PolymarketApplicableMarket => ({
  clobTokenIds: market.clobTokenIds,
  clobTokenNames: market.clobTokenNames,
  id: market.id,
  link: market.link,
  question: market.question,
  volume: market.volume,
  volume24hr: market.volume24hr ?? 0,
  bestBid: market.bestBid ?? 0,
  bestAsk: market.bestAsk ?? 0,
  negRisk: market.negRisk,
  hasFee: market.hasFee,
  tags: internPolymarketTags(market.tags),
});

const resolveSnapshotUrl = (snapshotUrl: string): string => {
  if (snapshotUrl.startsWith('http://') || snapshotUrl.startsWith('https://')) {
    return snapshotUrl;
  }
  if (snapshotUrl.startsWith('/')) {
    return `${POLYMARKET_MARKETS_BASE_URL}${snapshotUrl}`;
  }
  return `${POLYMARKET_CATALOG_BASE_URL}/${snapshotUrl}`;
};

const decompressGzipSnapshot = async (
  buffer: ArrayBuffer,
): Promise<PolymarketCatalogSnapshot> => {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Browser does not support gzip snapshot decompression');
  }
  const stream = new Blob([buffer])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));
  return (await new Response(stream).json()) as PolymarketCatalogSnapshot;
};

const parseSnapshotResponse = async (
  response: Response,
): Promise<PolymarketCatalogSnapshot> => {
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;

  const snapshot = isGzip
    ? await decompressGzipSnapshot(buffer)
    : (JSON.parse(
        new TextDecoder().decode(buffer),
      ) as PolymarketCatalogSnapshot);

  return {
    ...snapshot,
    markets: snapshot.markets.map(normalizePolymarketMarket),
  };
};

export const fetchPolymarketMarketsPage = async ({
  limit,
  cursor,
  q,
  seed,
  signal,
}: FetchPolymarketMarketsPageParams = {}): Promise<PolymarketMarketsPage> => {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (q) params.set('q', q);
  if (seed) params.set('seed', 'true');

  const response = await fetch(
    `${POLYMARKET_MARKETS_BASE_URL}/markets?${params.toString()}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(`Failed to load Polymarket markets: ${response.status}`);
  }
  const page = (await response.json()) as PolymarketMarketsPage;
  return { ...page, markets: page.markets.map(normalizePolymarketMarket) };
};

export const fetchPolymarketMarketById = async (
  conditionId: string,
): Promise<PolymarketApplicableMarket | null> => {
  const response = await fetch(
    `${POLYMARKET_MARKETS_BASE_URL}/markets/${encodeURIComponent(conditionId)}`,
  );
  // Closed/resolved markets still appear in news matches but are absent from
  // the applicable catalog — treat 404 as a soft miss, not a hard failure.
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to load Polymarket market: ${response.status}`);
  }
  return normalizePolymarketMarket(
    (await response.json()) as PolymarketApplicableMarket,
  );
};

/**
 * Fetches just the long-form description for a single market on demand. The
 * description is intentionally excluded from the in-memory catalog (see
 * normalizePolymarketMarket), so the trade view loads it lazily for the
 * currently selected market only.
 */
export const fetchPolymarketMarketDescription = async (
  conditionId: string,
  signal?: AbortSignal,
): Promise<string> => {
  const response = await fetch(
    `${POLYMARKET_MARKETS_BASE_URL}/markets/${encodeURIComponent(conditionId)}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(`Failed to load Polymarket market: ${response.status}`);
  }
  const data = (await response.json()) as { description?: string };
  return typeof data.description === 'string' ? data.description : '';
};

export const fetchPolymarketCatalogManifest = async (
  signal?: AbortSignal,
): Promise<PolymarketCatalogManifest> => {
  const response = await fetch(
    `${POLYMARKET_MARKETS_BASE_URL}/catalog/manifest`,
    {
      signal,
    },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to load Polymarket catalog manifest: ${response.status}`,
    );
  }
  return (await response.json()) as PolymarketCatalogManifest;
};

export const fetchPolymarketCatalogSnapshot = async (
  snapshotUrl: string,
  signal?: AbortSignal,
): Promise<PolymarketCatalogSnapshot> => {
  const response = await fetch(resolveSnapshotUrl(snapshotUrl), { signal });
  if (!response.ok) {
    throw new Error(
      `Failed to load Polymarket catalog snapshot: ${response.status}`,
    );
  }
  return parseSnapshotResponse(response);
};

export const fetchPolymarketSeedFromProxy = async (
  signal?: AbortSignal,
): Promise<PolymarketCatalogSnapshot> => {
  try {
    return await fetchPolymarketCatalogSnapshot(
      POLYMARKET_CATALOG_SNAPSHOT_FALLBACK_PATH,
      signal,
    );
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
  }

  const markets: PolymarketApplicableMarket[] = [];
  let cursor: string | null = null;
  let updatedAt: string | null = null;
  let pages = 0;
  const maxPages = 200;

  do {
    const page = await fetchPolymarketMarketsPage({
      limit: 1_000,
      cursor,
      seed: true,
      signal,
    });
    if (page.updatedAt) updatedAt = page.updatedAt;
    markets.push(...page.markets);
    cursor = page.nextCursor;
  } while (cursor && ++pages < maxPages);

  if (!updatedAt) {
    throw new Error('Polymarket proxy seed catalog is not ready');
  }

  return {
    schemaVersion: 1,
    updatedAt,
    total: markets.length,
    markets,
  };
};
